import React, { useEffect, useState, useMemo } from 'react';
import { withTranslation } from 'react-i18next';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import { loadServiceTypes, saveAddedServices } from '../actionCreators';
import OfferFinancialServicesAddServicesComponent from './OfferFinancialServicesAddServicesComponent';
import debounce from 'lodash.debounce';

export const OfferFinancialServicesAddServicesContainer = ({ showModal, onCloseAvailableServicesModal, documentId, siteId }) => {
  const [localServiceTypesList, setLocalServiceTypesList] = useState([]);
  const [search, setSearch] = useState('');
  const dispatch = useDispatch();
  const DEBOUNCE_DELAY = 500;

  const addServices = () => {
    let addedServices = localServiceTypesList.filter(x => x.selected === true).map(x => x.serviceTypeId);
    dispatch(saveAddedServices(documentId, siteId, addedServices));
    onCloseModal();
  };

  function selectMany(f) {
    return function (acc, b) {
      return acc.concat(f(b));
    };
  }
  const serviceTypesList = useSelector(
    state => (state.currentOffer.financialServicesData ? state.currentOffer.financialServicesData.serviceTypesList : []),
    shallowEqual
  );

  const serviceList = useMemo(() => {
    if (!search) return localServiceTypesList;
    return localServiceTypesList.filter(item => item.serviceName.toUpperCase().includes(search.toUpperCase()));
  }, [localServiceTypesList, search]);

  const serviceAreSelected = useMemo(() => {
    return !serviceList.some(x => !x.selected && !x.disabled);
  }, [serviceList]);

  const existingServices = useSelector(state => state.currentOffer.financialServicesData?.document?.services ?? [], shallowEqual);

  const existingCategoryServices = useSelector(
    state => state.currentOffer.financialServicesData?.document?.categoryServices ?? [],
    shallowEqual
  );

  let servicesFromCategories = existingCategoryServices.reduce(
    selectMany(a => a.services),
    []
  );

  let allServices = existingServices.concat(servicesFromCategories);

  const serviceLinkingList = useSelector(state => state.currentOffer.financialServicesData?.serviceLinkingList ?? [], shallowEqual);

  const serviceLinkingListFiltered = serviceLinkingList.filter(el => {
    return allServices.some(f => {
      return f.serviceTypeId === el.sourceServiceTypeId || f.serviceTypeId === el.destServiceTypeId;
    });
  });

  const onCloseModal = () => {
    onCloseAvailableServicesModal();
  };
  const serviceTypesListLoaded = useSelector(
    state => state.currentOffer.financialServicesData?.serviceTypesListLoaded ?? false,
    shallowEqual
  );

  const handleServiceTypeSelect = service => e => {
    const index = localServiceTypesList.findIndex(item => item.serviceTypeId === service.serviceTypeId);

    setLocalServiceTypesList(
      Object.assign([], {
        ...localServiceTypesList,
        [index]: {
          ...localServiceTypesList[index],
          selected: e
        }
      })
    );
  };

  const handleSelectAll = event => {
    setLocalServiceTypesList(state =>
      state.map(item => {
        if (serviceList.some(service => service.serviceTypeId === item.serviceTypeId))
          return { ...item, selected: event.target.checked };
        return item;
      })
    );
  };

  const handleSearch = value => setSearch(value);
  const handleSearchDebounced = debounce(handleSearch, DEBOUNCE_DELAY);
  const handleResetFilters = () => setSearch('');
  useEffect(() => {
    if (!serviceTypesListLoaded) return;

    setLocalServiceTypesList(serviceTypesList);
  }, [serviceTypesListLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(loadServiceTypes(documentId, siteId));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <OfferFinancialServicesAddServicesComponent
      showModal={showModal}
      serviceTypesList={serviceList}
      serviceLinkingList={serviceLinkingListFiltered}
      onCloseModal={onCloseModal}
      onServiceTypeSelected={handleServiceTypeSelect}
      addServices={addServices}
      onSelectAllServiceTypesChange={handleSelectAll}
      onSearchChange={handleSearchDebounced}
      search={search}
      onResetFilters={handleResetFilters}
      serviceAreSelected={serviceAreSelected}
    />
  );
};

OfferFinancialServicesAddServicesContainer.propTypes = {
  showModal: PropTypes.bool.isRequired,
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  onCloseAvailableServicesModal: PropTypes.func.isRequired
};

export default withTranslation()(OfferFinancialServicesAddServicesContainer);
